import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react'
import { supabase } from './supabase'
import { trackLogin, performLogout } from './lib/analytics'
import { createScopedDb, SocietaContext, PermessiContext, GuidaContext, RED, GOLD, LIGHT, NAV_ITEMS, DEFAULT_PAGE_BY_RUOLO } from './components/shared'
import { getTourForRuolo, GUIDA_VERSION } from './components/guideSteps'
import NovitaAvvio from './components/NovitaAvvio'

// ── Code-splitting: ogni pagina viene caricata on-demand ──
// Factory che conserva la promise per il preload su hover (non ricarica più volte)
const lazyWithPreload = (factory) => {
  const Comp = lazy(factory)
  Comp.preload = factory
  return Comp
}
const Dashboard      = lazyWithPreload(() => import('./components/Dashboard'))
const Giocatori      = lazyWithPreload(() => import('./components/Giocatori'))
const Allenatori     = lazyWithPreload(() => import('./components/Allenatori'))
const Presenze       = lazyWithPreload(() => import('./components/Presenze'))
const Squadre        = lazyWithPreload(() => import('./components/Squadre'))
const Calendario     = lazyWithPreload(() => import('./components/Calendario'))
const Pagamenti      = lazyWithPreload(() => import('./components/Pagamenti'))
const Tornei         = lazyWithPreload(() => import('./components/Tornei'))
const Comunicazioni  = lazyWithPreload(() => import('./components/Comunicazioni'))
const Email          = lazyWithPreload(() => import('./components/Email'))
const Dirigenti      = lazyWithPreload(() => import('./components/Dirigenti'))
const GestioneUtenti = lazyWithPreload(() => import('./components/GestioneUtenti'))
const Documenti      = lazyWithPreload(() => import('./components/Documenti'))
const Abbigliamento  = lazyWithPreload(() => import('./components/Abbigliamento'))
const Configurazione = lazyWithPreload(() => import('./components/Configurazione'))
const Importazione   = lazyWithPreload(() => import('./components/Importazione'))
const Impostazioni   = lazyWithPreload(() => import('./components/Impostazioni'))
const StampePDF      = lazyWithPreload(() => import('./components/StampePDF'))
const Convocazioni   = lazyWithPreload(() => import('./components/Convocazioni'))
const Profilo        = lazyWithPreload(() => import('./components/Profilo'))
const Permessi       = lazyWithPreload(() => import('./components/Permessi'))
const StatisticheGiocatori = lazyWithPreload(() => import('./components/StatisticheGiocatori'))
const Compensi      = lazyWithPreload(() => import('./components/Compensi'))
const Disciplina    = lazyWithPreload(() => import('./components/Disciplina'))
const Infortuni     = lazyWithPreload(() => import('./components/Infortuni'))
const CentriEstivi  = lazyWithPreload(() => import('./components/CentriEstivi'))
const News          = lazyWithPreload(() => import('./components/News'))
const Sponsor       = lazyWithPreload(() => import('./components/Sponsor'))
const IscrizioneCentroEstivo = lazy(() => import('./components/IscrizioneCentroEstivo'))
const Sito = lazy(() => import('./components/Sito'))
const PassaggioStagione = lazy(() => import('./components/PassaggioStagione'))
const InstallaApp    = lazyWithPreload(() => import('./components/InstallaApp'))
const Accademia      = lazyWithPreload(() => import('./components/Accademia'))
const CoseDaFare     = lazyWithPreload(() => import('./components/CoseDaFare'))
const GuidaInterattiva = lazy(() => import('./components/GuidaInterattiva'))
const SuperAdmin       = lazyWithPreload(() => import('./components/SuperAdmin'))
const Wallet           = lazyWithPreload(() => import('./components/Wallet'))
const Piani            = lazyWithPreload(() => import('./components/Piani'))
const GestioneAbbonamenti = lazyWithPreload(() => import('./components/GestioneAbbonamenti'))
const SelezioneSocieta = lazy(() => import('./components/SuperAdmin').then(m => ({ default: m.SelezioneSocieta })))

// Mappa id-nav → lazy component per il preload su hover
const LAZY_BY_ID = {
  dashboard: Dashboard, giocatori: Giocatori, allenatori: Allenatori, presenze: Presenze,
  convocazioni: Convocazioni, squadre: Squadre, calendario: Calendario, pagamenti: Pagamenti,
  tornei: Tornei, comunicazioni: Comunicazioni, email: Email, utenti: GestioneUtenti, documenti: Documenti,
  stampe: StampePDF, abbigliamento: Abbigliamento, configurazione: Configurazione,
  impostazioni: Impostazioni, importazione: Importazione, superadmin: SuperAdmin, dirigenti: Dirigenti,
  profilo: Profilo, permessi: Permessi, statistiche: StatisticheGiocatori, compensi: Compensi,
  disciplina: Disciplina, infortuni: Infortuni, centriestivi: CentriEstivi,
  news: News, sponsor: Sponsor,
}
const preloadPagina = (id) => { try { LAZY_BY_ID[id]?.preload?.() } catch {} }

// ── Fallback usato mentre il chunk della pagina viene scaricato ──
function PageFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: '#bbb' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>⚽</div>
        <div style={{ fontSize: 12 }}>Caricamento...</div>
      </div>
    </div>
  )
}

// ── LOGIN ──────────────────────────────────────────────────
// Campo password con "occhio" per mostrare/nascondere il testo digitato.
// Fondamentale per utenti non tecnici: vedono subito se il telefono ha messo
// la maiuscola da solo o se c'è uno spazio di troppo (causa n.1 di login falliti).
function PasswordInput({ value, onChange, onKeyDown, placeholder = '••••••••' }) {
  const [visibile, setVisibile] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <input type={visibile ? 'text' : 'password'} value={value} onChange={onChange} onKeyDown={onKeyDown} placeholder={placeholder}
        style={{ width: '100%', padding: '10px 42px 10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
      <button type="button" tabIndex={-1} onClick={() => setVisibile(v => !v)}
        aria-label={visibile ? 'Nascondi password' : 'Mostra password'}
        title={visibile ? 'Nascondi password' : 'Mostra password'}
        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 17, padding: '4px 6px', color: '#888', lineHeight: 1 }}>
        {visibile ? '🙈' : '👁️'}
      </button>
    </div>
  )
}

function Login({ onLogin, initialMode = null }) {
  const [mode, setMode] = useState(initialMode || 'login') // 'login' | 'signup' | 'tenant'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [nome, setNome] = useState('')
  const [cognome, setCognome] = useState('')
  const [nomeSocieta, setNomeSocieta] = useState('')
  const [citta, setCitta] = useState('')
  const [errore, setErrore] = useState('')
  const [info, setInfo] = useState('')
  const [caricamento, setCaricamento] = useState(false)
  const [mostraGuidaInstallazione, setMostraGuidaInstallazione] = useState(false)

  const creaSocieta = async () => {
    setErrore(''); setInfo('')
    const em = (email || '').trim()
    if (!em) { setErrore('Inserisci un indirizzo email.'); return }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { setErrore('Email non valida.'); return }
    if (password.length < 8) { setErrore('La password deve essere di almeno 8 caratteri.'); return }
    if (!nome.trim() || !cognome.trim()) { setErrore('Nome e cognome sono obbligatori.'); return }
    if (!nomeSocieta.trim()) { setErrore('Inserisci il nome della societa.'); return }
    setCaricamento(true)
    try {
      const { data, error } = await supabase.functions.invoke('signup-tenant', {
        body: {
          email: em,
          password,
          nome: nome.trim(),
          cognome: cognome.trim(),
          nome_societa: nomeSocieta.trim(),
          citta: citta.trim(),
        },
      })
      if (error) {
        // L'errore di functions.invoke include lo status; il body con il messaggio
        // chiaro e' in error.context.body o data
        let msg = error.message || 'Errore durante la creazione'
        try {
          const ctx = error.context && (await error.context.json?.())
          if (ctx?.error) msg = ctx.error
        } catch { /* ignore */ }
        setErrore(msg)
        setCaricamento(false); return
      }
      if (data?.error) { setErrore(data.error); setCaricamento(false); return }
      // Login automatico
      const { data: signin, error: errSignin } = await supabase.auth.signInWithPassword({ email: em, password })
      if (errSignin) {
        setInfo('Societa creata! Ora puoi accedere.')
        setMode('login'); setCaricamento(false); return
      }
      const { data: profilo } = await supabase.from('profili').select('*').eq('id', signin.user.id).maybeSingle()
      if (profilo) { await onLogin(profilo); trackLogin().catch(() => {}); setCaricamento(false); return }
      setInfo('Societa creata! Accedi con le tue credenziali.')
      setMode('login')
    } catch (e) {
      setErrore('Errore di rete: ' + (e?.message || e))
    }
    setCaricamento(false)
  }

  const registrati = async () => {
    setErrore(''); setInfo('')
    const em = (email || '').trim()
    if (!em) { setErrore('Inserisci un indirizzo email.'); return }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { setErrore('Email non valida.'); return }
    if (password.length < 8) { setErrore('La password deve essere di almeno 8 caratteri.'); return }
    if (!nome.trim() || !cognome.trim()) { setErrore('Nome e cognome sono obbligatori.'); return }
    setCaricamento(true)
    const { data, error } = await supabase.auth.signUp({
      email: em,
      password,
      options: { data: { nome: nome.trim(), cognome: cognome.trim(), ruolo: 'admin', deve_cambiare_password: false } },
    })
    if (error) {
      setErrore('Registrazione non riuscita: ' + error.message)
      setCaricamento(false); return
    }
    const identities = data?.user?.identities
    if (Array.isArray(identities) && identities.length === 0) {
      setErrore('Esiste già un account con questa email. Prova ad accedere.')
      setCaricamento(false); return
    }
    // Notifica admin + mail di benvenuto (best-effort: non bloccano la registrazione)
    inviaMailDemo({ nome: nome.trim(), cognome: cognome.trim(), email: em })
    if (data?.session) {
      const { data: profilo } = await supabase.from('profili').select('*').eq('id', data.user.id).maybeSingle()
      if (profilo) { await onLogin(profilo); setCaricamento(false); return }
    }
    setInfo('Account creato. Ora puoi accedere.')
    setMode('login')
    setCaricamento(false)
  }

  // Invia 2 mail via edge function send-email (Resend):
  // 1) notifica admin a info@sportly.cloud
  // 2) benvenuto al nuovo utente demo
  const inviaMailDemo = async ({ nome, cognome, email }) => {
    // Dal 28/09/2026 la "modalità sistema" di send-email richiede un utente
    // autenticato (PIANO.md § 1.2): dal browser non si può più usare la chiave
    // email globale della piattaforma senza accesso. Qui l'utente ha appena
    // creato l'account, quindi ha già una sessione (se la conferma email è
    // disattivata). Senza sessione si salta l'invio: meglio nessuna mail che una
    // porta aperta a chiunque.
    const { data: sessione } = await supabase.auth.getSession()
    if (!sessione?.session) return
    const ADMIN_EMAIL = 'info@sportly.cloud'
    const APP_URL = 'https://sportly.vercel.app/'
    const LOGO_URL = 'https://sportly.vercel.app/logo.svg'
    const FOOTER_ADDR = 'Sportly Ltd — 123 O\'Connell Street, Dublin 1, Ireland'
    const dataIso = new Date().toLocaleString('it-IT')

    const htmlAdmin = `
      <div style="font-family:Calibri,'Segoe UI',Arial,sans-serif;max-width:560px;color:#222">
        <h2 style="color:#D0021B;margin:0 0 12px">Nuovo iscritto Demo</h2>
        <p style="margin:0 0 6px"><b>Nome:</b> ${nome} ${cognome}</p>
        <p style="margin:0 0 6px"><b>Email:</b> ${email}</p>
        <p style="margin:0 0 6px"><b>Data:</b> ${dataIso}</p>
      </div>`

    const htmlBenvenuto = `
      <div style="font-family:Calibri,'Segoe UI',Arial,sans-serif;max-width:600px;color:#222;line-height:1.55">
        <div style="text-align:center;padding:20px 0;border-bottom:3px solid #D0021B">
          <img src="${LOGO_URL}" alt="Sportly" style="height:56px" />
        </div>
        <div style="padding:24px 8px">
          <h2 style="color:#D0021B;margin:0 0 12px">Benvenuto in Sportly, ${nome}!</h2>
          <p>Grazie per aver creato un account di prova. Sportly è il gestionale pensato per le società di calcio: anagrafiche, presenze, calendario, pagamenti, convocazioni e molto altro — tutto in un unico posto.</p>
          <p>Stai provando il software nella società <b>Demo</b>, condivisa con altri utenti in prova. Sentiti libero di esplorare: nessun dato reale verrà compromesso.</p>
          <p style="text-align:center;margin:24px 0">
            <a href="${APP_URL}" style="background:#D0021B;color:#fff;text-decoration:none;padding:12px 24px;border-radius:8px;font-weight:600;display:inline-block">Accedi a Sportly</a>
          </p>
          <p style="color:#666;font-size:13px">Se hai domande o vuoi una demo guidata, rispondi a questa mail: ti risponderemo al più presto.</p>
        </div>
        <div style="border-top:1px solid #eee;padding:14px 8px;color:#888;font-size:12px;text-align:center">
          ${FOOTER_ADDR}
        </div>
      </div>`

    try {
      await supabase.functions.invoke('send-email', {
        body: {
          to: ADMIN_EMAIL,
          from: `Sportly <${ADMIN_EMAIL}>`,
          subject: `Nuovo iscritto Demo: ${nome} ${cognome}`,
          html: htmlAdmin,
        },
      })
    } catch { /* best effort */ }
    try {
      await supabase.functions.invoke('send-email', {
        body: {
          to: email,
          from: `Sportly <${ADMIN_EMAIL}>`,
          subject: 'Benvenuto in Sportly — il tuo account demo è pronto',
          html: htmlBenvenuto,
        },
      })
    } catch { /* best effort */ }
  }

  const accedi = async () => {
    setCaricamento(true)
    setErrore('')
    const { data, error } = await supabase.auth.signInWithPassword({ email: (email || '').trim(), password })
    if (error) {
      // Messaggio più chiaro in base al tipo di errore riportato da Supabase
      const raw = (error.message || '').toLowerCase()
      let msg = 'Email o password non corretti'
      if (raw.includes('email not confirmed') || raw.includes('confirm')) {
        msg = 'Email non ancora confermata. Controlla la casella di posta (anche la cartella Spam) e clicca sul link di conferma ricevuto da Supabase.'
      } else if (raw.includes('invalid login') || raw.includes('invalid credentials')) {
        msg = 'Email o password non corretti. Controlla che la password sia stata copiata senza spazi all\'inizio o alla fine.'
      } else if (raw.includes('rate') || raw.includes('too many')) {
        msg = 'Troppi tentativi. Aspetta un minuto e riprova.'
      } else {
        msg = 'Accesso non riuscito: ' + error.message
      }
      setErrore(msg)
      setCaricamento(false)
      return
    }
    const { data: profilo } = await supabase.from('profili').select('*').eq('id', data.user.id).maybeSingle()
    if (!profilo) { setErrore('Profilo utente non trovato. Contatta l\'amministratore.'); setCaricamento(false); return }
    if (profilo.attivo === false) {
      await performLogout(supabase)
      setErrore('Il tuo account è stato disattivato. Contatta la segreteria della società.')
      setCaricamento(false); return
    }
    if (profilo.data_scadenza && new Date(profilo.data_scadenza) < new Date(new Date().toDateString())) {
      await performLogout(supabase)
      setErrore('Il tuo accesso di prova è scaduto. Contatta la società per riattivarlo.')
      setCaricamento(false); return
    }
    await onLogin(profilo)
    trackLogin().catch(() => {})
    setCaricamento(false)
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: LIGHT }}>
      <div style={{ background: 'white', borderRadius: 16, border: '0.5px solid #e0e0e0', padding: '40px 36px', width: 360 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/logo.svg" alt="Sportly" style={{ height: 40, margin: '0 auto 14px', display: 'block' }} />
          <div style={{ fontSize: 18, fontWeight: 500, color: '#222' }}>
            {mode === 'login' ? 'Accedi al gestionale' : mode === 'tenant' ? 'Crea la tua società' : 'Crea account demo'}
          </div>
          {mode === 'signup' && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 6, lineHeight: 1.4 }}>
              Proverai il gestionale nella società <b>Demo</b>, condivisa con altri utenti in prova.
            </div>
          )}
          {mode === 'tenant' && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 6, lineHeight: 1.4 }}>
              Crea la tua società: <b>15 giorni di prova gratuita</b>, database vuoto e dedicato.
            </div>
          )}
        </div>
        {errore && <div style={{ background: '#fdecea', border: `1px solid ${RED}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: RED }}>{errore}</div>}
        {info && <div style={{ background: '#e6f4ea', border: '1px solid #1a7a3a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#1a7a3a' }}>{info}</div>}
        {(mode === 'signup' || mode === 'tenant') && (
          <>
            <div style={{ marginBottom: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Nome*</label>
                <input type="text" value={nome} onChange={e => setNome(e.target.value)} placeholder="Mario"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Cognome*</label>
                <input type="text" value={cognome} onChange={e => setCognome(e.target.value)} placeholder="Rossi"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
            </div>
          </>
        )}
        {mode === 'tenant' && (
          <div style={{ marginBottom: 10, display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Nome della società*</label>
              <input type="text" value={nomeSocieta} onChange={e => setNomeSocieta(e.target.value)} placeholder="A.S.D. Mario Rossi"
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Città</label>
              <input type="text" value={citta} onChange={e => setCitta(e.target.value)} placeholder="Firenze"
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
          </div>
        )}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Email*</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && (mode === 'login' ? accedi() : mode === 'tenant' ? creaSocieta() : registrati())} placeholder="la-tua@email.it"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>
            Password* {(mode === 'signup' || mode === 'tenant') && <span style={{ color: '#aaa' }}>(min. 8 caratteri)</span>}
          </label>
          <PasswordInput value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && (mode === 'login' ? accedi() : mode === 'tenant' ? creaSocieta() : registrati())} />
        </div>
        <button onClick={mode === 'login' ? accedi : mode === 'tenant' ? creaSocieta : registrati} disabled={caricamento}
          style={{ width: '100%', background: RED, color: 'white', border: 'none', borderRadius: 10, padding: '12px', fontSize: 15, fontWeight: 500, cursor: 'pointer', opacity: caricamento ? 0.7 : 1 }}>
          {caricamento
            ? (mode === 'login' ? 'Accesso in corso...' : 'Creazione in corso...')
            : (mode === 'login' ? 'Accedi' : mode === 'tenant' ? 'Crea società (15gg gratis)' : 'Crea account')}
        </button>
        <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13, lineHeight: 1.7 }}>
          {mode === 'login' ? (
            <>
              <div>Non hai un account? <button onClick={() => { setMode('signup'); setErrore(''); setInfo('') }} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: 0 }}>Prova la Demo</button></div>
              <div>Vuoi creare la tua società? <button onClick={() => { setMode('tenant'); setErrore(''); setInfo('') }} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: 0 }}>Inizia 15 giorni gratis</button></div>
            </>
          ) : (
            <>Hai già un account? <button onClick={() => { setMode('login'); setErrore(''); setInfo('') }} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: 0 }}>Accedi</button></>
          )}
        </div>
        <button onClick={() => setMostraGuidaInstallazione(true)}
          style={{
            width: '100%', marginTop: 14, background: '#f5f5f5', color: '#444',
            border: '1px solid #ddd', borderRadius: 10, padding: '10px',
            fontSize: 13, fontWeight: 500, cursor: 'pointer',
          }}>
          📱 Come installare l'app sul telefono
        </button>
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#aaa' }}>Problemi? Contatta l'amministratore</div>
      </div>
      {mostraGuidaInstallazione && (
        <Suspense fallback={null}>
          <InstallaApp onClose={() => setMostraGuidaInstallazione(false)} />
        </Suspense>
      )}
    </div>
  )
}

// ── Schermata obbligatoria di cambio password al primo accesso ──
function CambioPassword({ utente, onDone }) {
  const [pw1, setPw1] = useState('')
  const [pw2, setPw2] = useState('')
  const [errore, setErrore] = useState('')
  const [caricamento, setCaricamento] = useState(false)

  const conferma = async () => {
    setErrore('')
    if (pw1.length < 8) { setErrore('La password deve essere di almeno 8 caratteri.'); return }
    if (pw1 !== pw2)    { setErrore('Le due password non coincidono.'); return }
    setCaricamento(true)
    const { error } = await supabase.auth.updateUser({ password: pw1 })
    if (error) { setErrore('Errore: ' + error.message); setCaricamento(false); return }
    const { error: err2 } = await supabase.from('profili').update({ deve_cambiare_password: false }).eq('id', utente.id)
    setCaricamento(false)
    if (err2) { setErrore('Password cambiata ma errore nel profilo: ' + err2.message); return }
    onDone()
  }

  const esci = async () => { await performLogout(supabase) }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: LIGHT }}>
      <div style={{ background: 'white', borderRadius: 16, border: '0.5px solid #e0e0e0', padding: '36px 32px', width: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🔐</div>
          <div style={{ fontSize: 19, fontWeight: 500, color: '#222' }}>Imposta la tua nuova password</div>
          <div style={{ fontSize: 13, color: '#888', marginTop: 6, lineHeight: 1.4 }}>
            Ciao {utente.nome}, per motivi di sicurezza al primo accesso devi scegliere una password personale.
          </div>
        </div>
        {errore && <div style={{ background: '#fdecea', border: `1px solid ${RED}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: RED }}>{errore}</div>}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Nuova password (min. 8 caratteri)</label>
          <PasswordInput value={pw1} onChange={e => setPw1(e.target.value)} onKeyDown={e => e.key === 'Enter' && conferma()} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Conferma nuova password</label>
          <PasswordInput value={pw2} onChange={e => setPw2(e.target.value)} onKeyDown={e => e.key === 'Enter' && conferma()} />
        </div>
        <button onClick={conferma} disabled={caricamento}
          style={{ width: '100%', background: RED, color: 'white', border: 'none', borderRadius: 10, padding: '12px', fontSize: 15, fontWeight: 500, cursor: 'pointer', opacity: caricamento ? 0.7 : 1, marginBottom: 10 }}>
          {caricamento ? 'Salvataggio...' : 'Salva e continua'}
        </button>
        <button onClick={esci}
          style={{ width: '100%', background: 'none', color: '#888', border: 'none', padding: '6px', fontSize: 12, cursor: 'pointer' }}>
          Esci senza cambiare
        </button>
      </div>
    </div>
  )
}

// Rileva se siamo su schermo mobile (< 768px). Sincronizzato con window resize.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 767px)')
    const onChange = (e) => setIsMobile(e.matches)
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange)
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange)
    }
  }, [])
  return isMobile
}

// Rotte pubbliche intercettate PRIMA della logica di auth/tenant:
//   - /centro-estivo/<societa_id>           → form iscrizione genitori (no login)
//   - /, /funzioni, /tariffe,
//     /configurazioni, /faq                 → sito marketing pubblico
//   - /login                                 → schermata di accesso
//   - /registrati                            → schermata di iscrizione
function getPublicRoute() {
  if (typeof window === 'undefined') return null
  const path = window.location.pathname.replace(/\/+$/, '') || '/'
  const ce = path.match(/^\/centro-estivo\/([0-9a-f-]{36})$/i)
  if (ce) return { tipo: 'centro-estivo', societaId: ce[1] }
  // Lingua: prefisso /en oppure /it (default it)
  const lang = path.startsWith('/en') ? 'en' : 'it'
  const stripped = path.replace(/^\/(en|it)/, '') || '/'
  if (stripped === '/')                  return { tipo: 'sito', anchor: null,             lang }
  if (stripped === '/funzioni')          return { tipo: 'sito', anchor: 'funzioni',       lang }
  if (stripped === '/tariffe')           return { tipo: 'sito', anchor: 'tariffe',        lang }
  if (stripped === '/configurazioni')    return { tipo: 'sito', anchor: 'configurazioni', lang }
  if (stripped === '/faq')               return { tipo: 'sito', anchor: 'faq',            lang }
  if (stripped === '/login')             return { tipo: 'auth', mode: 'login',            lang }
  if (stripped === '/registrati')        return { tipo: 'auth', mode: 'tenant',           lang }
  return null
}

// Dopo un deploy i chunk lazy vecchi non esistono più: un client con
// l'index.html in cache che prova ad aprire una pagina ottiene un
// ChunkLoadError e, senza boundary, React smonta tutto (pagina bianca).
// Qui intercettiamo l'errore e ricarichiamo una volta sola per prendere la
// versione nuova; se il problema persiste mostriamo un pulsante "Ricarica".
class ChunkErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { errore: null } }
  static getDerivedStateFromError(error) { return { errore: error } }
  componentDidMount() {
    // La pagina è montata correttamente: riarma l'auto-reload per i prossimi deploy.
    try { sessionStorage.removeItem('chunk_reload') } catch {}
  }
  componentDidCatch(error) {
    const msg = error?.message || ''
    const isChunk = error?.name === 'ChunkLoadError' || /loading chunk|dynamically imported module/i.test(msg)
    let giaRicaricato = false
    try {
      giaRicaricato = !!sessionStorage.getItem('chunk_reload')
      if (isChunk && !giaRicaricato) sessionStorage.setItem('chunk_reload', '1')
    } catch {}
    if (isChunk && !giaRicaricato) window.location.reload()
  }
  render() {
    if (this.state.errore) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', background: '#faf5f5' }}>
          <div style={{ textAlign: 'center', color: '#666' }}>
            <div style={{ fontSize: 30, marginBottom: 10 }}>⚽</div>
            <div style={{ fontSize: 14, marginBottom: 14 }}>È disponibile una nuova versione dell'app.</div>
            <button onClick={() => { try { sessionStorage.removeItem('chunk_reload') } catch {}; window.location.reload() }}
              style={{ background: '#D0021B', color: 'white', border: 'none', borderRadius: 8, padding: '9px 20px', cursor: 'pointer', fontSize: 14 }}>
              ↺ Ricarica
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  return <ChunkErrorBoundary><AppRoutes /></ChunkErrorBoundary>
}

function AppRoutes() {
  const route = getPublicRoute()
  if (route?.tipo === 'centro-estivo') {
    return (
      <Suspense fallback={<PageFallback />}>
        <IscrizioneCentroEstivo societaId={route.societaId} />
      </Suspense>
    )
  }
  if (route?.tipo === 'sito') {
    return (
      <Suspense fallback={<PageFallback />}>
        <Sito initialAnchor={route.anchor} lang={route.lang} />
      </Suspense>
    )
  }
  if (route?.tipo === 'auth') {
    return <AppGated initialLoginMode={route.mode} />
  }
  return <AppGated />
}

function SubscriptionBanner({ societa, ruolo, onApriWallet }) {
  if (!societa || ruolo === 'superadmin') return null
  const status = societa.subscription_status || 'active'
  if (status === 'active') return null

  const giorni = (toIso) => {
    if (!toIso) return null
    const ms = new Date(toIso).getTime() - Date.now()
    return Math.max(0, Math.ceil(ms / 86400000))
  }
  const cfg = (() => {
    if (status === 'trial') {
      const g = giorni(societa.trial_end)
      return { bg: '#e3f0fb', border: '#1565c0', color: '#0d47a1', icon: '🎁',
        msg: g === 0
          ? 'Il tuo trial scade oggi. Aggiungi credito per continuare senza interruzioni.'
          : `Trial in corso: ${g} ${g === 1 ? 'giorno rimanente' : 'giorni rimanenti'} (scade il ${new Date(societa.trial_end).toLocaleDateString('it-IT')}).` }
    }
    if (status === 'grace') {
      const g = giorni(societa.grace_until)
      return { bg: '#fff4e0', border: '#b25e00', color: '#7a3f00', icon: '⚠️',
        msg: `Periodo di grazia: ${g} ${g === 1 ? 'giorno rimanente' : 'giorni rimanenti'}. Ricarica il wallet per evitare la sospensione.` }
    }
    if (status === 'suspended') {
      return { bg: '#fdecea', border: RED, color: RED, icon: '🚫',
        msg: 'Abbonamento sospeso: non puoi modificare i dati. Contatta l\'amministratore o ricarica il wallet.' }
    }
    return { bg: '#f0f0f0', border: '#666', color: '#444', icon: 'ⓘ', msg: 'Abbonamento cancellato.' }
  })()

  return (
    <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 16 }}>{cfg.icon}</span>
      <div style={{ fontSize: 13, color: cfg.color, flex: 1, minWidth: 200 }}>{cfg.msg}</div>
      {(ruolo === 'admin' || ruolo === 'segreteria') && (
        <button onClick={onApriWallet}
          style={{ background: cfg.border, color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
          Vai al wallet →
        </button>
      )}
    </div>
  )
}

function AppGated({ initialLoginMode = null }) {
  const isMobile = useIsMobile()
  const [utente, setUtente] = useState(null)
  const [caricamento, setCaricamento] = useState(true)
  const [active, setActive] = useState("dashboard")
  // Su mobile la sidebar parte chiusa (è un overlay), su desktop aperta (affiancata al contenuto)
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile)
  const [logoUrl, setLogoUrl] = useState(null)
  const [stagioneAttiva, setStagioneAttiva] = useState(null)
  // Multi-tenant state
  const [societaId, setSocietaIdState] = useState(null)
  const [societa, setSocietaState] = useState(null)
  const [societaList, setSocietaList] = useState([])
  const [selezioneSocietaMode, setSelezioneSocietaMode] = useState(false)
  // True quando il superadmin entra dalle scorciatoie "Modalita superadmin" della
  // SelezioneSocieta: la sidebar mostra solo le 3 voci di gestione cross-tenant,
  // niente moduli del tenant. Si esce cliccando "Cambia società" nella topbar.
  const [modaSuperadmin, setModaSuperadmin] = useState(false)
  // Permessi dinamici: mappa { ruolo -> Set(moduliVisibili) }. Se vuoto usa i default statici.
  const [permessiPerRuolo, setPermessiPerRuolo] = useState(null)
  // Quando dalla Dashboard si clicca su una riga, vogliamo che la pagina destinazione
  // apra direttamente il dettaglio di quel record. Qui teniamo {page, id} finché
  // la pagina target non l'ha consumato.
  const [focusRecord, setFocusRecord] = useState(null)
  const [showPassaggio, setShowPassaggio] = useState(false)
  const [guidaAperta, setGuidaAperta] = useState(false)
  // Guida "a compito" avviata da una pagina (es. Accademy): passi diversi dal giro
  // delle pagine. null = nessuna guida a compito in corso.
  const [guideSteps, setGuideSteps] = useState(null)

  // Verifica inline: stagione attiva "YYYY/YYYY" e oggi è >= 1 luglio di un anno successivo.
  const needsPassaggio = (() => {
    if (!stagioneAttiva?.nome) return false
    const m = String(stagioneAttiva.nome).match(/^(\d{4})\/(\d{4})$/)
    if (!m) return false
    const secondYearOfActive = Number(m[2])
    const today = new Date()
    const currentSecondYear = today.getMonth() >= 6 ? today.getFullYear() + 1 : today.getFullYear()
    return today.getMonth() >= 6 && today.getDate() >= 1 && secondYearOfActive < currentSecondYear
  })()
  const puoGestireStagione = utente && !['genitore', 'atleta'].includes(utente.ruolo)

  // Navigazione con focus opzionale su un record specifico (es: Giocatore id=xxx).
  // Usato dalla Dashboard per saltare direttamente alla scheda della persona.
  const navigateTo = useCallback((page, id = null) => {
    setActive(page)
    setFocusRecord(id ? (typeof id === 'object' ? { page, ...id } : { page, id }) : null)
    if (isMobile) setSidebarOpen(false)
  }, [isMobile])

  // scoped db client: auto-inietta societa_id negli INSERT/UPSERT.
  // Memoizzato per evitare loop di re-render nei consumer del SocietaContext.
  const db = useMemo(() => (societaId ? createScopedDb(societaId) : supabase), [societaId])
  const societaCtxValue = useMemo(() => ({ societaId, societa, db, setSocieta: setSocietaState }), [societaId, societa, db])

  // Stagioni della società: la attiva + l'elenco completo per il "gira pagina"
  // (frecce ‹ › in sidebar). stagioneVista = quella che l'utente sta guardando
  // (default: la attiva); le pagine di consultazione ricevono questa, mentre le
  // logiche di scrittura (quote, transizioni) restano sulla stagione attiva.
  const [stagioniList, setStagioniList] = useState([])
  const [stagioneVistaId, setStagioneVistaId] = useState(null)
  const loadStagioneAttiva = useCallback(async () => {
    if (!societaId) { setStagioneAttiva(null); setStagioniList([]); setStagioneVistaId(null); return }
    const { data, error } = await db.from('stagioni').select('*').order('nome', { ascending: true })
    const list = (!error && data) || []
    setStagioniList(list)
    setStagioneAttiva(list.find(s => s.attiva) || null)
    setStagioneVistaId(null) // al cambio società/stagione si riparte dalla attiva
  }, [db, societaId])
  const stagioneVista = stagioniList.find(s => s.id === stagioneVistaId) || stagioneAttiva
  const idxVista = stagioniList.findIndex(s => s.id === (stagioneVista?.id || null))
  const cambiaStagioneVista = (delta) => {
    if (idxVista === -1) return
    const next = stagioniList[idxVista + delta]
    if (next) setStagioneVistaId(next.id === stagioneAttiva?.id ? null : next.id)
  }
  const inArchivio = !!(stagioneVista && stagioneAttiva && stagioneVista.id !== stagioneAttiva.id)

  const [sitoSlug, setSitoSlug] = useState('')
  useEffect(() => {
    if (!societaId) { setSitoSlug(''); return }
    let alive = true
    db.from('impostazioni').select('valore').eq('chiave', 'sito_slug').maybeSingle()
      .then(({ data }) => { if (alive) setSitoSlug((data?.valore || '').trim()) })
    return () => { alive = false }
  }, [db, societaId])
  const urlSitoPubblico = sitoSlug ? `https://${sitoSlug}.sportly.cloud` : 'https://demo.sportly.cloud'

  // Quando si passa da desktop a mobile o viceversa, sistemo lo stato della sidebar
  // (chiusa su mobile, aperta su desktop) per evitare layout rotti su rotazione schermo.
  useEffect(() => {
    setSidebarOpen(!isMobile)
  }, [isMobile])

  // Carica i permessi dal DB (mappa ruolo -> { moduli: Set, livelli: { modulo: 'nessuno'|'lettura'|'aggiunta'|'completo' } }).
  // Se la tabella è vuota o la query fallisce, restiamo sui default statici di NAV_ITEMS.
  const loadPermessi = useCallback(async () => {
    const { data, error } = await supabase.from('permessi_ruolo').select('ruolo, moduli, moduli_livelli')
    if (error || !data?.length) { setPermessiPerRuolo({}); return }
    const mappa = {}
    data.forEach(row => {
      const livelli = (row.moduli_livelli && typeof row.moduli_livelli === 'object') ? row.moduli_livelli : {}
      // Se manca moduli_livelli, ricostruiscilo dalla lista `moduli` legacy (tutti a "completo")
      if (!row.moduli_livelli && Array.isArray(row.moduli)) {
        row.moduli.forEach(m => { livelli[m] = 'completo' })
      }
      const moduli = new Set(Object.keys(livelli).filter(k => livelli[k] && livelli[k] !== 'nessuno'))
      mappa[row.ruolo] = { moduli, livelli }
    })
    setPermessiPerRuolo(mappa)
  }, [])

  // Dopo login: determina la società
  const inizializzaSocieta = useCallback(async (profilo) => {
    if (profilo.ruolo === 'superadmin') {
      // Superadmin: carica tutte le società e mostra selezione
      const { data: socs } = await supabase.from('societa').select('*, piano:piani(*)').eq('attiva', true).order('nome')
      setSocietaList(socs || [])
      if (socs?.length === 1) {
        setSocietaIdState(socs[0].id)
        setSocietaState(socs[0])
      } else {
        setSelezioneSocietaMode(true)
      }
    } else if (profilo.societa_id) {
      // Utente normale: usa la sua società
      const { data: soc } = await supabase.from('societa').select('*, piano:piani(*)').eq('id', profilo.societa_id).single()
      setSocietaIdState(profilo.societa_id)
      setSocietaState(soc || null)
    }
    // Se profilo.societa_id è null (vecchio DB senza multi-tenant), lascia null
  }, [])

  const handleLogin = useCallback(async (profilo) => {
    setUtente(profilo)
    // Porta l'utente alla prima pagina del suo ruolo (es. atleta → Profilo)
    const pagineVisibili = NAV_ITEMS.filter(n => n.ruoli.includes(profilo.ruolo)).map(n => n.id)
    const defaultPage = DEFAULT_PAGE_BY_RUOLO[profilo.ruolo] || pagineVisibili[0] || 'dashboard'
    setActive(pagineVisibili.includes(defaultPage) ? defaultPage : (pagineVisibili[0] || 'dashboard'))
    await inizializzaSocieta(profilo)
  }, [inizializzaSocieta])

  const handleSelezioneSocieta = useCallback((s, gotoPage) => {
    setSocietaIdState(s.id)
    setSocietaState(s)
    setSelezioneSocietaMode(false)
    if (gotoPage) {
      setActive(gotoPage)
      setModaSuperadmin(true)  // sidebar minimal, solo voci gestione
    } else {
      setModaSuperadmin(false)  // normale modalita' tenant
    }
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        const { data: profilo } = await supabase.from('profili').select('*').eq('id', session.user.id).single()
        if (profilo) {
          if (profilo.attivo === false) {
            await performLogout(supabase)
            alert('Il tuo account è stato disattivato. Contatta la segreteria della società.')
          } else if (profilo.data_scadenza && new Date(profilo.data_scadenza) < new Date(new Date().toDateString())) {
            await performLogout(supabase)
            alert('Il tuo accesso di prova è scaduto. Contatta la società per riattivarlo.')
          } else {
            setUtente(profilo)
            const pagineVisibili = NAV_ITEMS.filter(n => n.ruoli.includes(profilo.ruolo)).map(n => n.id)
            const defaultPage = DEFAULT_PAGE_BY_RUOLO[profilo.ruolo] || pagineVisibili[0] || 'dashboard'
            setActive(pagineVisibili.includes(defaultPage) ? defaultPage : (pagineVisibili[0] || 'dashboard'))
            await inizializzaSocieta(profilo)
          }
        }
      }
      setCaricamento(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        // Cleanup analytics_session_id come fallback: il caso normale è che
        // performLogout() lo abbia già rimosso, ma se signOut() arriva da
        // altra fonte (token expire server-side, altro tab) qui chiudiamo.
        try { localStorage.removeItem('analytics_session_id') } catch {}
        setUtente(null); setSocietaIdState(null); setSocietaState(null)
        setSocietaList([]); setSelezioneSocietaMode(false)
      }
    })
    return () => subscription.unsubscribe()
  }, [inizializzaSocieta])

  useEffect(() => {
    if (!utente) return
    loadStagioneAttiva()
    loadPermessi()
  }, [utente, db, loadStagioneAttiva, loadPermessi])

  // Il logo società ha un'unica fonte: la colonna `societa.logo_url` (data URL
  // base64), scritta via RPC `set_societa_logo`. Lo teniamo sincronizzato con la
  // società corrente così che toolbar, StampePDF e Impostazioni pescino tutti da
  // qui. Cambiando società (superadmin) o dopo un upload si aggiorna da solo.
  useEffect(() => { setLogoUrl(societa?.logo_url || null) }, [societa])

  // Heartbeat sessione: traccia il tempo di permanenza dell'utente nel gestionale.
  // Ping ogni 60s. Al primo ping la RPC crea una nuova sessione e ne restituisce l'id,
  // i ping successivi aggiornano "ultimo_ping".
  useEffect(() => {
    if (!utente) return
    let sessionId = null
    let alive = true
    const ping = async () => {
      if (!alive) return
      try {
        const { data } = await supabase.rpc('sessione_ping', { p_session_id: sessionId })
        if (data) sessionId = data
      } catch { /* silenzioso */ }
    }
    ping() // primo ping all'apertura
    const t = setInterval(ping, 60000)
    // Ultimo ping prima di chiudere la pagina (best-effort)
    const onUnload = () => { try { supabase.rpc('sessione_ping', { p_session_id: sessionId }) } catch {} }
    window.addEventListener('beforeunload', onUnload)
    return () => {
      alive = false
      clearInterval(t)
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [utente])

  // Auto-apertura guida al primo accesso (per utente + versione guida).
  // Se la versione cambia, la guida riappare anche a chi l'aveva già vista.
  useEffect(() => {
    if (!utente || selezioneSocietaMode) return
    try {
      const chiave = `sportly_guida_v${GUIDA_VERSION}_${utente.id}`
      if (!localStorage.getItem(chiave)) {
        setGuidaAperta(true)
      }
    } catch {}
  }, [utente, selezioneSocietaMode])

  const chiudiGuida = useCallback(() => {
    setGuidaAperta(false)
    setGuideSteps(null)
    if (utente) {
      try { localStorage.setItem(`sportly_guida_v${GUIDA_VERSION}_${utente.id}`, '1') } catch {}
    }
  }, [utente])

  const esci = async () => {
    await performLogout(supabase)
    setUtente(null); setSocietaIdState(null); setSocietaState(null)
  }

  if (caricamento) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: LIGHT }}>
      <div style={{ textAlign: 'center', color: '#aaa' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚽</div>
        <div style={{ fontSize: 14 }}>Caricamento...</div>
      </div>
    </div>
  )

  if (!utente) return <Login onLogin={handleLogin} initialMode={initialLoginMode} />

  // Se il profilo ha il flag "deve_cambiare_password", mostriamo la schermata obbligatoria.
  // Non fa passare all'app finché l'utente non ha scelto una password nuova.
  if (utente.deve_cambiare_password) return (
    <CambioPassword
      utente={utente}
      onDone={() => setUtente(u => ({ ...u, deve_cambiare_password: false }))}
    />
  )

  if (selezioneSocietaMode) return (
    <Suspense fallback={<PageFallback />}>
      <SelezioneSocieta societa={societaList} onSelect={handleSelezioneSocieta} ruolo={utente?.ruolo} />
    </Suspense>
  )

  // Filtro menu: se i permessi dinamici sono stati caricati dal DB uso quelli,
  // altrimenti fallback ai default statici in NAV_ITEMS.ruoli
  const isAdmin = utente.ruolo === 'admin' || utente.ruolo === 'superadmin'
  const permessiRuolo = permessiPerRuolo?.[utente.ruolo]
  const moduliConsentiti = permessiRuolo?.moduli
  const livelliPerModulo = permessiRuolo?.livelli || {}
  // Voci della "Modalita superadmin": niente moduli del tenant, solo gestione cross-tenant.
  const VOCI_SUPERADMIN = ['superadmin', 'abbonamenti', 'piani']
  const navVisibile = NAV_ITEMS.filter(n => {
    if (modaSuperadmin) {
      // Sidebar minimal per gestione cross-tenant
      return VOCI_SUPERADMIN.includes(n.id)
    }
    // Admin/superadmin: sempre visibilità piena sui moduli del loro ruolo, ignora la tabella permessi
    const abilitatoPerRuolo = isAdmin
      ? n.ruoli.includes(utente.ruolo)
      : (moduliConsentiti ? moduliConsentiti.has(n.id) : n.ruoli.includes(utente.ruolo))
    if (!abilitatoPerRuolo) return false
    // Se la voce richiede un flag sulla società, controlla che sia attivo
    if (n.societaFlag && !societa?.[n.societaFlag]) return false
    // Feature flag dal piano della società (bypass per superadmin per consentire supporto cross-tenant)
    if (n.featFlag && utente.ruolo !== 'superadmin') {
      const piano = societa?.piano
      // Se il tenant ha un piano, rispetta il flag; senza piano default permissivo
      if (piano && piano[n.featFlag] === false) return false
    }
    return true
  })
  const nomeSocieta = societa?.nome || 'Sportly'
  // Logo in sidebar: usa quello della società corrente (societa.logo_url, fonte
  // unica per-tenant). Se la società non ha un logo proprio (es. Demo o società
  // appena creata) mostra il logo Sportly.
  const displayLogoUrl = societa?.logo_url || '/logo.svg'
  // Se l'utente ha la pagina attiva impostata su un modulo non consentito dal suo ruolo,
  // la forziamo sulla prima voce visibile (safety-net oltre al filtro del menu).
  const activeConsentito = navVisibile.some(n => n.id === active) ? active : (navVisibile[0]?.id || 'dashboard')

  const renderContent = () => {
    switch (activeConsentito) {
      case "dashboard":     return <Dashboard utente={utente} onNavigate={navigateTo} />
      case "cosedafare":    return <CoseDaFare utente={utente} onNavigate={navigateTo} />
      case "superadmin":    return <SuperAdmin />
      case "piani":         return <Piani />
      case "abbonamenti":   return <GestioneAbbonamenti />
      case "giocatori":     return <Giocatori onNavigate={setActive} focusRecord={focusRecord?.page === 'giocatori' ? focusRecord : null} clearFocus={() => setFocusRecord(null)} stagioneAttiva={stagioneAttiva} />
      case "allenatori":    return <Allenatori focusRecord={focusRecord?.page === 'allenatori' ? focusRecord : null} clearFocus={() => setFocusRecord(null)} stagioneAttiva={stagioneAttiva} />
      case "dirigenti":     return <Dirigenti />
      case "presenze":      return <Presenze stagioneAttiva={stagioneVista} />
      case "convocazioni":  return <Convocazioni stagioneAttiva={stagioneVista} />
      case "profilo":       return <Profilo utente={utente} />
      case "squadre":       return <Squadre stagioneAttiva={stagioneAttiva} />
      case "calendario":    return <Calendario onNavigate={setActive} stagioneAttiva={stagioneVista} />
      case "wallet":        return <Wallet />
      case "pagamenti":     return <Pagamenti utente={utente} stagioneAttiva={stagioneAttiva} onStagioneChange={loadStagioneAttiva} />
      case "tornei":        return <Tornei stagioneAttiva={stagioneVista} />
      case "statistiche":   return <StatisticheGiocatori stagioneAttiva={stagioneVista} />
      case "comunicazioni": return <Comunicazioni utente={utente} />
      case "email":         return <Email />
      case "utenti":        return <GestioneUtenti />
      case "documenti":     return <Documenti />
      case "sponsor":       return <Sponsor stagioneAttiva={stagioneAttiva} />
      case "stampe":        return <StampePDF partite={[]} logoUrl={displayLogoUrl} />
      case "abbigliamento":   return <Abbigliamento />
      case "compensi":        return <Compensi />
      case "disciplina":      return <Disciplina stagioneAttiva={stagioneVista} />
      case "infortuni":       return <Infortuni stagioneAttiva={stagioneVista} />
      case "centriestivi":    return <CentriEstivi onNavigate={navigateTo} />
      case "accademia":       return <Accademia utente={utente} />
      case "news":            return <News />
      case "configurazione": return <Configurazione onStagioneChange={loadStagioneAttiva} />
      case "permessi":       return <Permessi onPermessiChanged={loadPermessi} />
      case "impostazioni":   return <Impostazioni logoUrl={logoUrl} setLogoUrl={setLogoUrl} ruolo={utente.ruolo} />
      case "importazione":   return <Importazione />
      default: return (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#bbb" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{navVisibile.find(n => n.id === activeConsentito)?.icon}</div>
          <div style={{ fontSize: 18, fontWeight: 500, color: "#ccc" }}>{navVisibile.find(n => n.id === activeConsentito)?.label}</div>
          <div style={{ fontSize: 13, marginTop: 8 }}>Modulo in sviluppo</div>
        </div>
      )
    }
  }

  // Quando si clicca una voce del menu su mobile, chiudiamo automaticamente la sidebar.
  const handleNavClick = (itemId) => {
    setActive(itemId)
    if (isMobile) setSidebarOpen(false)
  }

  // Stili sidebar: overlay (position: fixed + backdrop scuro) su mobile, inline su desktop.
  const sidebarStyle = isMobile
    ? {
        position: 'fixed', top: 0, left: 0, bottom: 0,
        width: 240, background: '#3d2020',
        display: 'flex', flexDirection: 'column',
        zIndex: 1001, boxShadow: '2px 0 12px rgba(0,0,0,0.3)',
      }
    : { width: 200, background: '#3d2020', display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'sticky', top: 0, height: '100vh', alignSelf: 'flex-start' }

  return (
    <SocietaContext.Provider value={societaCtxValue}>
    <PermessiContext.Provider value={{ livelloPerModulo: livelliPerModulo, isAdmin }}>
    <GuidaContext.Provider value={{ apriGuida: setGuideSteps }}>
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "system-ui, sans-serif", background: LIGHT, fontSize: 13 }}>
      {/* Avviso all'apertura: correzioni e novità dell'ultima versione (una volta sola) */}
      <NovitaAvvio ruolo={utente?.ruolo} />
      {/* Backdrop scuro dietro la sidebar su mobile per migliorare leggibilità */}
      {sidebarOpen && isMobile && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000 }} />
      )}
      {sidebarOpen && (
        <div style={sidebarStyle} data-guide="sidebar">
          <div style={{ padding: "12px 12px", borderBottom: "0.5px solid #4a2828" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <img src={displayLogoUrl || "/logo.svg"} alt={nomeSocieta} style={{ height: 32, flexShrink: 0, objectFit: 'contain', maxWidth: 48 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "#ffffff", fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nomeSocieta}</div>
                {utente.ruolo === 'superadmin' && societaList.length > 1 && (
                  <button onClick={() => { setSelezioneSocietaMode(true); setModaSuperadmin(false) }}
                    style={{ background: 'none', border: 'none', color: GOLD, fontSize: 9, cursor: 'pointer', padding: 0 }}>
                    ↔ Cambia società
                  </button>
                )}
              </div>
              {isMobile && (
                <button onClick={() => setSidebarOpen(false)}
                  style={{ background: 'none', border: 'none', color: '#888', fontSize: 20, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
              )}
            </div>
          </div>
          <nav style={{ flex: 1, padding: "6px 0", overflowY: "auto" }}>
            {navVisibile.map((item, i) => {
              const prev = navVisibile[i - 1]
              const showHeader = item.section && (!prev || prev.section !== item.section)
              return (
                <React.Fragment key={item.id}>
                  {showHeader && (
                    <div style={{ padding: i === 0 ? "4px 14px 4px" : "14px 14px 4px", fontSize: 9, fontWeight: 700, letterSpacing: 1.2, color: "#ffd0a0", textTransform: "uppercase" }}>
                      {item.section}
                    </div>
                  )}
                  <div
                    data-guide={`nav-${item.id}`}
                    onClick={() => handleNavClick(item.id)}
                    onMouseEnter={e => { preloadPagina(item.id); if (activeConsentito !== item.id) e.currentTarget.style.background = '#5a2e2e' }}
                    onMouseLeave={e => { if (activeConsentito !== item.id) e.currentTarget.style.background = 'transparent' }}
                    onFocus={() => preloadPagina(item.id)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", cursor: "pointer", background: activeConsentito === item.id ? RED : "transparent", borderLeft: activeConsentito === item.id ? `3px solid ${GOLD}` : "3px solid transparent", color: "#ffffff", fontSize: 13, fontWeight: activeConsentito === item.id ? 700 : 500 }}>
                    <span style={{ fontSize: 14 }}>{item.icon}</span>
                    {item.label}
                  </div>
                </React.Fragment>
              )
            })}
          </nav>
          <div style={{ padding: "10px 12px", borderTop: "0.5px solid #6a3a3a" }}>
            {/* "Gira pagina" tra le stagioni: le frecce cambiano la stagione in
                visione in tutta l'app; quelle passate sono in sola consultazione. */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <button onClick={() => cambiaStagioneVista(-1)} disabled={idxVista <= 0}
                title="Stagione precedente"
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid #6a3a3a", borderRadius: 5, color: idxVista <= 0 ? "#7a5a5a" : "#ffd0a0", cursor: idxVista <= 0 ? "default" : "pointer", fontSize: 12, padding: "1px 7px", lineHeight: 1.4 }}>‹</button>
              <div style={{ flex: 1, textAlign: "center", fontSize: 11, color: inArchivio ? "#ffb020" : "#ffd0a0", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {stagioneVista ? `Stagione ${stagioneVista.nome}` : "Nessuna stagione"}
              </div>
              <button onClick={() => cambiaStagioneVista(1)} disabled={idxVista === -1 || idxVista >= stagioniList.length - 1}
                title="Stagione successiva"
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid #6a3a3a", borderRadius: 5, color: (idxVista === -1 || idxVista >= stagioniList.length - 1) ? "#7a5a5a" : "#ffd0a0", cursor: (idxVista === -1 || idxVista >= stagioniList.length - 1) ? "default" : "pointer", fontSize: 12, padding: "1px 7px", lineHeight: 1.4 }}>›</button>
            </div>
            {inArchivio && (
              <button onClick={() => setStagioneVistaId(null)}
                style={{ width: "100%", background: "#ffb020", border: "none", borderRadius: 6, padding: "4px 6px", cursor: "pointer", color: "#3d2020", fontSize: 10.5, fontWeight: 700, marginBottom: 6 }}>
                📁 ARCHIVIO — torna alla stagione attiva
              </button>
            )}
            <a href={urlSitoPubblico} target="_blank" rel="noopener noreferrer" data-guide="vai-al-sito"
              style={{ display: "block", textAlign: "center", textDecoration: "none", width: "100%", background: "rgba(255,255,255,0.08)", border: "1px solid #6a3a3a", borderRadius: 6, padding: "6px", cursor: "pointer", color: "#ffffff", fontSize: 11, fontWeight: 500, marginBottom: 6, boxSizing: "border-box" }}>
              🌐 Vai al sito
            </a>
            <button onClick={esci} style={{ width: "100%", background: "rgba(255,255,255,0.08)", border: "1px solid #6a3a3a", borderRadius: 6, padding: "6px", cursor: "pointer", color: "#ffffff", fontSize: 11, fontWeight: 500 }}>Esci</button>
          </div>
        </div>
      )}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ background: "white", borderBottom: "0.5px solid #e0e0e0", padding: isMobile ? "0 12px" : "0 20px", height: isMobile ? 46 : 52, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12, minWidth: 0, flex: 1 }}>
            <button onClick={() => setSidebarOpen(o => !o)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: isMobile ? 20 : 18, color: "#555", padding: 4, flexShrink: 0 }}>☰</button>
            <span style={{ fontWeight: 500, fontSize: isMobile ? 14 : 15, color: "#333", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{navVisibile.find(n => n.id === activeConsentito)?.label}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10, flexShrink: 0 }}>
            {societa && !isMobile && <span style={{ fontSize: 11, color: '#888', background: '#f5f5f5', borderRadius: 5, padding: '2px 8px' }}>🏟️ {societa.nome}</span>}
            <button
              data-guide="header-guide"
              onClick={() => setGuidaAperta(true)}
              title="Riapri guida"
              style={{ background: 'none', border: '1px solid #ddd', borderRadius: '50%', width: isMobile ? 26 : 28, height: isMobile ? 26 : 28, cursor: 'pointer', color: '#666', fontSize: 13, fontWeight: 600, padding: 0, flexShrink: 0 }}>
              ?
            </button>
            <div style={{ width: isMobile ? 26 : 30, height: isMobile ? 26 : 30, borderRadius: "50%", background: RED, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: isMobile ? 10 : 12, fontWeight: 500, flexShrink: 0 }}>
              {utente.nome?.[0]}{utente.cognome?.[0]}
            </div>
            {!isMobile && (
              <span style={{ fontSize: 13, color: "#555" }}>{utente.nome} · <span style={{ color: GOLD, fontWeight: 500 }}>{utente.ruolo}</span></span>
            )}
          </div>
        </div>
        <div style={{ flex: 1, padding: isMobile ? "14px 12px" : "24px 28px", overflowY: "auto" }}>
          <SubscriptionBanner societa={societa} ruolo={utente.ruolo} onApriWallet={() => setActive('wallet')} />
          {needsPassaggio && puoGestireStagione && (
            <div style={{ background: '#fff8dc', border: `1px solid ${GOLD}`, borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, color: '#6b4a00' }}>
                È iniziata una nuova stagione sportiva. Fai il passaggio di stagione per promuovere tutti gli atleti.
              </div>
              <button onClick={() => setShowPassaggio(true)}
                style={{ background: RED, color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                Passaggio di stagione
              </button>
            </div>
          )}
          <Suspense fallback={<PageFallback />}>
            {renderContent()}
          </Suspense>
        </div>
        {(guidaAperta || guideSteps) && (
          <Suspense fallback={null}>
            <GuidaInterattiva
              steps={guideSteps || getTourForRuolo(utente.ruolo, utente.nome)}
              navigateTo={(id) => { setActive(id); if (isMobile) setSidebarOpen(true) }}
              onFinish={chiudiGuida}
            />
          </Suspense>
        )}
        {showPassaggio && (
          <Suspense fallback={null}>
            <PassaggioStagione
              stagioneAttiva={stagioneAttiva}
              societaId={societaId}
              db={db}
              onClose={() => setShowPassaggio(false)}
              onDone={() => loadStagioneAttiva()}
            />
          </Suspense>
        )}
      </div>
    </div>
    </GuidaContext.Provider>
    </PermessiContext.Provider>
    </SocietaContext.Provider>
  )
}
